import { useQuery } from '@tanstack/react-query';
import { useContext, useMemo } from 'react';
import { PageContext } from 'src/context/PageContext';
import { getCustomerFeedParticipationCounts } from 'src/service/engageFrontendGateway';
import { FeedItem } from 'types/feeds';
import { LogError } from 'utils/loggingHelpers';

interface UseParticipationCountsOptions {
  feed: FeedItem[];
  feedType: string;
}

export const useParticipationCounts = ({ feed, feedType }: UseParticipationCountsOptions) => {
  const { page, urlParams } = useContext(PageContext);
  const { customer_id: customerId } = page || {};
  const { organization, locale } = urlParams || {};
  const engagementIds = useMemo(() => feed.filter((item) => item.type === 'Engagement').map((item) => item.id), [feed]);
  const discussionTopicIds = useMemo(
    () => feed.filter((item) => item.type === 'DiscussionTopic').map((item) => item.id),
    [feed],
  );
  const ideaBoardIds = useMemo(() => feed.filter((item) => item.type === 'IdeaBoard').map((item) => item.id), [feed]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['participationCounts', customerId, engagementIds, discussionTopicIds, ideaBoardIds],
    queryFn: async () => {
      if (!organization || !locale || !customerId) return {};

      try {
        const participationCounts = await getCustomerFeedParticipationCounts(customerId, {
          engagementIds,
          discussionTopicIds,
          ideaBoardIds,
        });

        return participationCounts;
      } catch (err) {
        // This may be indicating the EFG or ERS is not working
        console.error(err);
        LogError('Error fetching participation counts', err);
        throw err;
      }
    },
    initialData: {},
    enabled: feedType === 'engagement_activity' && feed.length > 0 && !!customerId,
  });

  return {
    participationCounts: data || {},
    isLoadingParticipationCounts: isLoading,
    participationCountsError: error,
  };
};
